import { useCallback, useContext, useMemo, useState } from "react";
import { ActiveFarmContext } from "./context";

export const useActiveFarmContext = () => useContext(ActiveFarmContext);

export const useRememberedFieldSelection = (fields = []) => {
  const { activeFarm, lastSelectedFieldId, setLastSelectedFieldId } =
    useActiveFarmContext();
  const activeFarmId = activeFarm ? String(activeFarm.id) : null;
  const [localSelection, setLocalSelection] = useState({
    farmId: null,
    fieldId: null,
  });

  const selectedFieldId = useMemo(() => {
    if (!fields.length) return null;

    const localFieldId =
      localSelection.farmId === activeFarmId ? localSelection.fieldId : null;

    if (
      localFieldId &&
      fields.some((field) => String(field.getId()) === String(localFieldId))
    ) {
      return String(localFieldId);
    }

    if (
      lastSelectedFieldId &&
      fields.some(
        (field) => String(field.getId()) === String(lastSelectedFieldId)
      )
    ) {
      return String(lastSelectedFieldId);
    }

    return String(fields[0].getId());
  }, [activeFarmId, fields, lastSelectedFieldId, localSelection]);

  const selectedField = useMemo(() => {
    if (!selectedFieldId) return null;
    return (
      fields.find((field) => String(field.getId()) === selectedFieldId) || null
    );
  }, [fields, selectedFieldId]);

  const fieldIndex = useMemo(() => {
    if (!selectedFieldId) return -1;
    return fields.findIndex(
      (field) => String(field.getId()) === String(selectedFieldId)
    );
  }, [fields, selectedFieldId]);

  const setSelectedFieldId = useCallback(
    (fieldOrId) => {
      const normalized =
        fieldOrId && typeof fieldOrId === "object"
          ? String(fieldOrId.getId?.() ?? fieldOrId.id ?? "")
          : fieldOrId
            ? String(fieldOrId)
            : null;

      setLocalSelection({ farmId: activeFarmId, fieldId: normalized });

      if (normalized) {
        setLastSelectedFieldId(normalized);
      }
    },
    [activeFarmId, setLastSelectedFieldId]
  );

  return {
    selectedFieldId,
    selectedField,
    fieldIndex,
    setSelectedFieldId,
  };
};
